import { PALETTE_AURORA } from '@shared/builtinPulseMaps/palettes';
import {
  ceilingYAt,
  floorYAt,
  WAVES_DEFAULT_SPAWN_Y,
  WAVES_VARIANT_TUNING,
  type WavesChart,
  type WavesGate,
  type WavesTerrain,
  type WavesTerrainNode,
} from '@shared/wavesChart';
import type { ChartAudio } from '@shared/chart';

/**
 * Hand-crafted teaching chart for `/waves/tutorial`. Four sections,
 * each anchored to a beat range so the React-side overlay text can
 * advance in sync with the chart:
 *
 *   beats  4-10  basic spike threading (floor spike, ceiling spike,
 *                floor spike — practises hold-then-release rhythm)
 *   beats 11-17  tunnel sections (slider equivalent — corridor
 *                pinches mid-screen so the player learns to centre)
 *   beats 18-22  combined floor + ceiling spikes at a tighter cadence
 *   beats 23-27  freeplay tail before the "you've got it" card
 *
 * Audio reuses `my-heart.mp3` at the same 100 BPM as the Guitar
 * tutorial — keeps a player who tutorials each mode in succession in
 * the same musical headspace.
 *
 * Tuning copies the Normal variant of the imported set so a player
 * who clears the tutorial steps into the easiest tier at the same
 * speed they just trained on.
 */
const TUTORIAL_BPM = 100;

/**
 * Half-height of the tutorial's winding corridor (playfield units). A
 * fixed-width corridor whose midline rolls up and down, matching the
 * generated-map model in `wavesFromCircles.ts` (just hand-keyed here so
 * the teaching beats land where the overlays expect them).
 */
const TUTORIAL_HALF_CORRIDOR = 160;

/**
 * Midline keyframes for the tutorial terrain. The corridor centre
 * follows these points (floor = mid + half, ceiling = mid − half), so a
 * fixed-width tunnel snakes through the playfield. Two keyframes sharing
 * a beat encode a vertical step: an UP step (smaller `mid`) raises the
 * floor into a lethal wall the player must climb above.
 *
 *   beats  0–24  flat centre (spike + tunnel teaching reads cleanly)
 *   beats 24–34  gentle roll up/down (teaches the non-lethal glide)
 *   beats 34–37  flat runway into the wall
 *   beat     37  lethal floor wall (mid steps 360 → 250)
 *   beats 37–44  settle on the raised floor to the finish card
 */
const TUTORIAL_MIDLINE: ReadonlyArray<{ beat: number; mid: number }> = [
  { beat: 0, mid: 360 },
  { beat: 24, mid: 360 },
  { beat: 27, mid: 300 },
  { beat: 30, mid: 420 },
  { beat: 34, mid: 360 },
  { beat: 37, mid: 360 },
  { beat: 37, mid: 250 },
  { beat: 44, mid: 250 },
];

function buildTutorialTerrain(): WavesTerrain {
  const floor: WavesTerrainNode[] = TUTORIAL_MIDLINE.map((k) => ({
    beat: k.beat,
    y: k.mid + TUTORIAL_HALF_CORRIDOR,
  }));
  const ceiling: WavesTerrainNode[] = TUTORIAL_MIDLINE.map((k) => ({
    beat: k.beat,
    y: k.mid - TUTORIAL_HALF_CORRIDOR,
  }));
  return { floor, ceiling };
}

function makeFloorSpike(id: string, beat: number, terrain: WavesTerrain): WavesGate {
  const minGap = WAVES_VARIANT_TUNING.normal.minGapHeight;
  const top = ceilingYAt(terrain, beat);
  return {
    id,
    kind: 'gate',
    beat,
    gapTopY: top,
    gapBottomY: top + minGap,
    visual: 'spike-floor',
  };
}

function makeCeilingSpike(id: string, beat: number, terrain: WavesTerrain): WavesGate {
  const minGap = WAVES_VARIANT_TUNING.normal.minGapHeight;
  const bottom = floorYAt(terrain, beat);
  return {
    id,
    kind: 'gate',
    beat,
    gapTopY: bottom - minGap,
    gapBottomY: bottom,
    visual: 'spike-ceiling',
  };
}

function makeTunnelGate(
  id: string,
  beat: number,
  gapMul: number,
  terrain: WavesTerrain,
): WavesGate {
  const gapH = WAVES_VARIANT_TUNING.normal.minGapHeight * gapMul;
  const mid = (ceilingYAt(terrain, beat) + floorYAt(terrain, beat)) / 2;
  return {
    id,
    kind: 'gate',
    beat,
    gapTopY: mid - gapH / 2,
    gapBottomY: mid + gapH / 2,
    visual: 'pillar',
  };
}

export function buildTutorialWavesChart(): WavesChart {
  const tuning = WAVES_VARIANT_TUNING.normal;
  const audio: ChartAudio = {
    src: '/audio/waves/my-heart.mp3',
    bpm: TUTORIAL_BPM,
    offsetMs: 0,
    durationSec: 60,
    attribution:
      'Tutorial uses "Heart Afire" by Defqwop (feat. Strix) [NCS Release] - https://ncs.io/HeartAfire',
    onsets: [],
  };

  const terrain = buildTutorialTerrain();

  const obstacles = [
    // Section A — single spikes with generous spacing (overlay 1).
    // Floor → ceiling → floor establishes the basic up/down rhythm.
    makeFloorSpike('t-01', 4, terrain),
    makeCeilingSpike('t-02', 7, terrain),
    makeFloorSpike('t-03', 10, terrain),

    // Section B — tunnel section (overlay 2). The corridor pinches
    // mid-screen; the player has to track the corridor centre.
    makeTunnelGate('t-04', 12, 0.9, terrain),
    makeTunnelGate('t-05', 12.5, 0.9, terrain),
    makeTunnelGate('t-06', 13, 0.9, terrain),
    makeTunnelGate('t-07', 13.5, 0.9, terrain),
    makeTunnelGate('t-08', 14, 0.9, terrain),

    // Section C — alternating spikes at a tighter cadence (overlay 3).
    makeFloorSpike('t-09', 18, terrain),
    makeCeilingSpike('t-10', 19.5, terrain),
    makeFloorSpike('t-11', 21, terrain),
    makeCeilingSpike('t-12', 22, terrain),

    // Sections D + E (beats 24–44) carry no obstacles: the rolling
    // terrain teaches the glide, then the lone floor wall at beat 37
    // teaches the climb. A clear runway keeps the wall fair.
  ];

  return {
    mode: 'waves',
    id: 'tutorial-waves',
    slug: 'tutorial-waves',
    title: 'How to play (Waves)',
    description:
      'Quick teaching chart on a slow track. Hold to climb, release to descend.',
    bpm: TUTORIAL_BPM,
    difficulty: 1,
    variant: 'normal',
    totalBeats: 44,
    palette: PALETTE_AURORA,
    audio,
    spawnY: WAVES_DEFAULT_SPAWN_Y,
    playerVyPerBeat: tuning.vYPerBeat,
    playerRadius: tuning.playerRadius,
    obstacles,
    terrain,
  };
}

/**
 * Overlay instructions for the tutorial page, each anchored to a beat
 * range. Renderer in {@link import('../../pages/waves/WavesTutorialPage').WavesTutorialPage}
 * shows the most recent overlay whose `fromBeat <= currentBeat <
 * untilBeat`.
 */
export interface WavesTutorialOverlay {
  fromBeat: number;
  untilBeat: number;
  title: string;
  body: string;
}

/**
 * Returns the overlay sequence with input wording adapted to the
 * device. Touch devices get "press and hold" copy; keyboard devices
 * see Space as the primary binding (Waves shares the Pulse key set so
 * a multi-mode player has one button to learn).
 */
export function getWavesTutorialOverlays(isTouch: boolean): WavesTutorialOverlay[] {
  const holdVerb = isTouch ? 'Touch and hold' : 'Hold Space';
  const releaseVerb = isTouch ? 'release' : 'release Space';
  return [
    {
      fromBeat: -8,
      untilBeat: 4,
      title: 'Wave through the corridor',
      body: `${holdVerb} to fly up, ${releaseVerb} to fall. One touch on any obstacle ends your run.`,
    },
    {
      fromBeat: 4,
      untilBeat: 11,
      title: 'Dodge spikes by climbing or diving',
      body: 'Floor spikes force you up; ceiling spikes force you down. Time your hold to swap sides cleanly.',
    },
    {
      fromBeat: 11,
      untilBeat: 18,
      title: 'Centre the corridor for tunnels',
      body: 'Tunnels pinch the corridor narrow. Find the middle and pulse small ups and downs to stay there.',
    },
    {
      fromBeat: 18,
      untilBeat: 24,
      title: 'Rhythm-locked alternation',
      body: "Every spike lands on a real beat — let the music tell you when to flip rather than reacting to the obstacle.",
    },
    {
      fromBeat: 24,
      untilBeat: 31,
      title: 'Glide the rolling terrain',
      body: 'The floor and ceiling roll up and down. Riding along the slopes is safe — let the corridor carry you.',
    },
    {
      fromBeat: 31,
      untilBeat: 40,
      title: 'Climb above the wall',
      body: 'A sheer vertical wall is lethal. Rise above its top edge before you reach it — slopes glide, walls kill.',
    },
    {
      fromBeat: 40,
      untilBeat: Number.POSITIVE_INFINITY,
      title: "You\u2019re ready",
      body: 'Free play to the end of the bar, then pick a real track from the home screen.',
    },
  ];
}
